import React from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

const BORDER_COLOR = "#E8ECF1";
const DEFAULT_ACCENT = "#2DB838";

export default function ConversationEmptyState({
  title = "Nothing to show",
  description = "There are no conversations available yet.",
  loading = false,
  error = "",
  eyebrow = "Inbox",
  icon = null,
  actionLabel = "",
  onAction = null,
  accentColor = DEFAULT_ACCENT,
}) {
  const resolvedIcon = icon ?? <MailOutlineRoundedIcon sx={{ fontSize: 28 }} />;
  const safeAction = typeof onAction === "function" ? onAction : null;

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: BORDER_COLOR,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "white",
        backgroundImage:
          `radial-gradient(circle at top, ${alpha(accentColor, 0.1)}, transparent 48%), linear-gradient(180deg, #ffffff 0%, #f8fdf9 100%)`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 4, py: 5.5, textAlign: "center", maxWidth: 500 }}>
        {loading ? (
          <>
            <CircularProgress size={34} sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight={900}>
              Loading conversations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
              Fetching the latest channel activity.
            </Typography>
          </>
        ) : error ? (
          <>
            <Typography
              variant="overline"
              sx={{ display: "block", letterSpacing: 1.6, fontWeight: 900, color: "error.main", mb: 1 }}
            >
              {eyebrow}
            </Typography>
            <Typography variant="h6" fontWeight={900}>
              Unable to load inbox
            </Typography>
            <Typography variant="body2" color="error.main" sx={{ mt: 0.8 }}>
              {error}
            </Typography>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2,
                borderRadius: "24px",
                display: "grid",
                placeItems: "center",
                color: accentColor,
                bgcolor: alpha(accentColor, 0.12),
                boxShadow: `inset 0 0 0 1px ${alpha(accentColor, 0.16)}`,
              }}
            >
              {resolvedIcon}
            </Box>
            <Typography
              variant="overline"
              sx={{ display: "block", letterSpacing: 1.6, fontWeight: 900, color: accentColor, mb: 1 }}
            >
              {eyebrow}
            </Typography>
            <Typography variant="h6" fontWeight={900}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
              {description}
            </Typography>
            {actionLabel && safeAction ? (
              <Button
                variant="contained"
                sx={{
                  mt: 2.4,
                  px: 2.4,
                  bgcolor: accentColor,
                  boxShadow: `0 10px 24px -14px ${alpha(accentColor, 0.7)}`,
                  "&:hover": { bgcolor: accentColor, filter: "brightness(0.94)" },
                }}
                onClick={safeAction}
              >
                {actionLabel}
              </Button>
            ) : null}
          </>
        )}
      </Box>
    </Paper>
  );
}
